import { motion } from "framer-motion";
import { useRef } from "react";
import { useInView } from "framer-motion";
import { Shield, Smartphone, Camera, Brain, Lock, Users, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Policy Section Component
const PolicySection = ({ icon: Icon, title, children, testId }) => {
  return (
    <AnimatedSection className="mb-12">
      <div className="flex items-start gap-4" data-testid={testId}>
        <div className="flex-shrink-0 w-12 h-12 bg-[#F3F4F6] flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#0055FF]" />
        </div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
            {title}
          </h2>
          <div className="text-gray-600 leading-relaxed font-light space-y-3">
            {children}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212]">
              LabrHood<span className="text-[#0055FF]">Labs</span>
            </span>
          </a>
          <Button 
            asChild
            variant="ghost"
            className="text-sm font-medium"
            data-testid="back-home-btn"
          >
            <a href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-white border-t border-gray-100" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <span className="font-heading text-lg font-bold tracking-tight text-[#121212] mb-4">
            LabrHood<span className="text-[#0055FF]">Labs</span>
          </span>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function PrivacyPolicy() {
  const lastUpdated = "January 28, 2026";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Hero */}
          <AnimatedSection className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
              Legal
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#121212] mb-6" data-testid="privacy-headline">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600 font-light mb-4">
              This Privacy Policy applies to the LabrHood Labs website (labrhood.com) and the Vibr Labs mobile application.
            </p>
            <p className="text-sm text-gray-500">
              Last Updated: {lastUpdated}
            </p>
          </AnimatedSection>

          {/* Introduction */}
          <AnimatedSection className="mb-12">
            <p className="text-gray-600 leading-relaxed font-light">
              At LabrHood Labs, we are committed to protecting your privacy and ensuring transparency about how we handle information. This policy outlines our practices regarding data collection, usage, and protection across our website and mobile applications.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Policy Sections */}
          <PolicySection 
            icon={Shield} 
            title="Data Minimization" 
            testId="policy-data-minimization"
          >
            <p>
              We believe in collecting only what is absolutely necessary. <strong>We do not collect personal data such as names, email addresses, or other identifying information</strong> unless you voluntarily choose to contact us through our inquiry form.
            </p>
            <p>
              When you submit an inquiry, we only collect the information you provide (name, email, company, and message) solely for the purpose of responding to your request. This data is not used for marketing purposes or shared with third parties.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Smartphone} 
            title="Arcade & Gameplay Data" 
            testId="policy-gameplay"
          >
            <p>
              Your gaming experience is yours alone. <strong>All in-game progress, high scores, achievements, and gameplay data are stored locally on your device.</strong>
            </p>
            <p>
              We do not sync, upload, or store your gameplay data on our servers. This means your progress remains private and under your control. If you uninstall the app or clear app data, this locally stored information will be removed from your device.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Camera} 
            title="Camera Usage (Snap Scan Feature)" 
            testId="policy-camera"
          >
            <p>
              The "Snap Scan" feature in our application uses your device's camera exclusively to identify food items for inventory management and recipe suggestions.
            </p>
            <p>
              <strong>Important:</strong> Photos captured through this feature are processed locally on your device or through secure, anonymized AI processing. <strong>We do not upload, store, or retain any private photos on our servers.</strong> Your images remain on your device and are never transmitted to third parties.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Brain} 
            title="AI Processing" 
            testId="policy-ai"
          >
            <p>
              Our AI-powered coaching and recipe suggestion features are designed with privacy at their core. The AI processes inventory data solely to provide personalized recipes and culinary recommendations.
            </p>
            <p>
              <strong>The AI does not track, identify, or profile individual users.</strong> All processing is performed on anonymized data, and no personal identity information is associated with the AI coaching functionality.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Lock} 
            title="No Third-Party Sales" 
            testId="policy-no-sales"
          >
            <p>
              Your trust is paramount to us. <strong>We do not sell, rent, trade, or otherwise transfer your personal information to third parties for commercial purposes.</strong>
            </p>
            <p>
              We do not engage in data brokering, advertising profiling, or any practice that would compromise your privacy for financial gain. Your data is used exclusively to provide and improve our services.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Users} 
            title="Target Audience" 
            testId="policy-audience"
          >
            <p>
              <strong>Our applications are designed for users aged 18 years and older.</strong> We do not knowingly collect information from children under 18.
            </p>
            <p>
              If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will take steps to remove such information from our systems.
            </p>
          </PolicySection>

          <div className="thin-divider mb-12" />

          {/* Contact Section */}
          <AnimatedSection>
            <div className="bg-[#F9FAFB] p-8 sm:p-12">
              <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
                Questions or Concerns?
              </h2>
              <p className="text-gray-600 leading-relaxed font-light mb-6">
                If you have any questions about this Privacy Policy or our data practices, please don't hesitate to reach out to us.
              </p>
              <Button 
                asChild
                className="btn-electric px-8 py-6 text-base font-medium"
                data-testid="contact-cta"
              >
                <a href="/#contact">Contact Us</a>
              </Button>
            </div>
          </AnimatedSection>

          {/* Policy Updates Notice */}
          <AnimatedSection className="mt-12">
            <p className="text-sm text-gray-500 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this page periodically for the latest information on our privacy practices.
            </p>
          </AnimatedSection>
        </div>
      </main>

      <Footer />
    </div>
  );
}
